<script setup>
// Datepicker dedicado pra VOIP: formato pt-BR (DD/MM/YYYY + 24h) + botoes rapidos
// "+1d", "+3d", "+7d" etc que saltam X dias pra frente mantendo a hora.
// v-model aceita/emite string ISO ('2026-04-22T14:30' — datetime-local compativel)
// ou null quando vazio.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  inputId: { type: String, default: undefined },
  modelValue: { type: [String, null], default: null },
  placeholder: { type: String, default: 'Selecione data e horário' },
  // Hora padrao quando so data e escolhida (default: 9h da manha)
  defaultHour: { type: Number, default: 9 },
  // Oculta botoes rapidos quando nao fazem sentido (edicao de data passada)
  showQuickButtons: { type: Boolean, default: true },
});
const emit = defineEmits(['update:modelValue']);

const dateStr = ref(''); // 'YYYY-MM-DD'
const hour    = ref('09');
const minute  = ref('00');

// Parse inicial do modelValue ISO (se vier preenchido)
function parseFromISO(iso) {
  if (!iso) { dateStr.value = ''; hour.value = String(props.defaultHour).padStart(2, '0'); minute.value = '00'; return; }
  // datetime-local: YYYY-MM-DDTHH:MM
  const [d, t] = iso.split('T');
  dateStr.value = d || '';
  const [h, m] = (t || '').split(':');
  hour.value = (h || '09').padStart(2, '0');
  minute.value = (m || '00').padStart(2, '0');
}
parseFromISO(props.modelValue);

watch(() => props.modelValue, parseFromISO);

// Emite ISO sempre que componentes mudam
function emitValue() {
  if (!dateStr.value) { emit('update:modelValue', null); return; }
  const value = `${dateStr.value}T${hour.value}:${minute.value}`;
  if (value !== props.modelValue) emit('update:modelValue', value);
}

watch([dateStr, hour, minute], emitValue);

// Format humano: "21/04/2026 às 14:30" — so pra display no label
const humanLabel = computed(() => {
  if (!dateStr.value) return '';
  const [y, m, d] = dateStr.value.split('-');
  return `${d}/${m}/${y} às ${hour.value}:${minute.value}`;
});

// Quick buttons: soma N dias a PARTIR de HOJE (nao da data atual do picker)
// Mantem a hora ja selecionada. Se nao tem data, usa hoje + 09h.
function addDays(n) {
  const base = new Date();
  base.setDate(base.getDate() + n);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');
  dateStr.value = `${y}-${m}-${d}`;
  if (!hour.value) hour.value = String(props.defaultHour).padStart(2, '0');
}

// Todos os minutos permanecem selecionáveis, inclusive horários já salvos.
const hourOpts   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const minuteOpts = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const quickButtons = [
  { label: 'Hoje',      days: 0 },
  { label: '+1 dia',    days: 1 },
  { label: '+3 dias',   days: 3 },
  { label: '+7 dias',   days: 7 },
  { label: '+15 dias',  days: 15 },
  { label: '+30 dias',  days: 30 },
];
</script>

<template>
  <div role="group" class="space-y-2">
    <!-- Quick buttons -->
    <div v-if="showQuickButtons" class="flex flex-wrap gap-1.5">
      <button v-for="qb in quickButtons" :key="qb.label" type="button"
              class="text-xs px-2 py-1 rounded border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12"
              @click="addDays(qb.days)">
        {{ qb.label }}
      </button>
    </div>

    <!-- Inputs: date + hour + minute -->
    <div class="flex flex-wrap items-center gap-2">
      <input :id="inputId" :aria-label="t('VOIP_DEVICES.OVERVIEW.DATE_FIELD')" v-model="dateStr" type="date"
             class="min-w-0 flex-1 rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12" />
      <div class="flex items-center gap-1">
        <select :aria-label="t('VOIP_DEVICES.OVERVIEW.HOUR_FIELD')" v-model="hour" class="rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12">
          <option v-for="h in hourOpts" :key="h" :value="h">{{ h }}</option>
        </select>
        <span class="text-n-slate-11 text-sm">:</span>
        <select :aria-label="t('VOIP_DEVICES.OVERVIEW.MINUTE_FIELD')" v-model="minute" class="rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12">
          <option v-for="m in minuteOpts" :key="m" :value="m">{{ m }}</option>
        </select>
      </div>
    </div>

    <!-- Preview humano -->
    <p v-if="humanLabel" class="text-[11px] text-n-slate-11">📅 {{ humanLabel }}</p>
    <p v-else class="text-[11px] text-n-slate-11 italic">{{ placeholder }}</p>
  </div>
</template>
